import React, {useMemo, useState} from 'react';
import {
    ValuesList,
    Table,
    Row,
    Cell,
    CardList,
    CardRow,
    CardKey,
    CardValue,
    Title,
    LoadingText,
    PreviewTitle, PreviewSubtitle
} from "./styles";
import {CircularProgress, FormControlLabel, Switch} from "@mui/material";
import {sections, allKeys} from "./sections";
import { Button } from '../common/button';
import {useTranslation} from "react-i18next";
import {Input} from "../common";
import {Toast} from "../common/toast";

interface Props {
    fileName?: string;
    data: any;
    editMode: boolean;
    toggleEditMode: () => void;
    loading?: boolean;
    fileLoadError?: boolean;
    onStartUpload: () => void;
    onChange: (invoiceIndex: number, key: string, value: string) => void;
    onItemChange: (invoiceIndex: number, index: number, key: string, value: string) => void;
}

const formatAmount = (obj: any) =>{
    if(obj === undefined || obj === null) return '-';
    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'ILS',
        currencyDisplay: 'symbol',
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
        useGrouping: true
    });
    return formatter.format(obj).replace('₪', '');
}

const getRow = (obj: any, type: string | undefined = undefined) => {
    if (Array.isArray(obj)) {
        let res = '';
        obj.forEach(item => {
            res += `${getRow(item)} `;
        })
        return res;
    }
    if (typeof obj === 'object') {

    }
    if(type === 'amount'){
        return formatAmount(obj);
    }
    return obj;
}

const PdfData: React.FC<Props> = (props: Props) => {
    const {data, loading, fileName, fileLoadError, onStartUpload, editMode, toggleEditMode, onChange, onItemChange} = props;
    const {t} = useTranslation();

    // A bundled PDF (several documents in one file) answers {invoices: [entry...]}: one
    // entry per document, the single-document payload (result, error, priority / message)
    // plus its pages. The pager below walks the entries - ported from the yousef_Haddad
    // branch, where the entries are the results themselves.
    const [currentInvoiceIndex, setCurrentInvoiceIndex] = useState(0);
    const entries: any[] | null = useMemo(
        () => (data?.invoices && Array.isArray(data.invoices) ? data.invoices : null), [data]);
    const currentEntry = useMemo(
        () => (entries ? (entries[currentInvoiceIndex] || null) : data), [entries, currentInvoiceIndex, data]);
    const currentInvoice = currentEntry?.result;
    const totalInvoices = entries ? entries.length : 1;

    const itemKeys = useMemo(() => {
        const availableKeys: { key: string; label: string; type?: string; }[] = [];
        if(currentInvoice?.items && currentInvoice.items.length > 0) {
            currentInvoice.items.forEach((item: any) => {
                Object.keys(item).forEach(key => {
                    if(-1 === availableKeys.findIndex(x => x.key === key)) {
                        availableKeys.push({key, label: key});
                    }
                });
            });
        }
        return availableKeys;
    }, [currentInvoice]);

    const getSectionRow = (section: { title: string; key?: string; items?: { key: string; label: string; type?: string; }[] }) => {
        if (!currentInvoice) return null;

        if (section.key === 'items') {
            if (currentInvoice.items && currentInvoice.items.length > 0) {
                return (<>
                    <Title>{t(section.title)}</Title>
                    <Table>
                        <Row header>
                            {
                                itemKeys.map(header => <Cell>
                                    <CardKey>
                                        {header.key.replaceAll('_', ' ')}
                                    </CardKey>
                                </Cell>)
                            }
                        </Row>
                        {
                            currentInvoice.items.map((item: any, i: number) => <Row key={`row-${i}`}>
                                {
                                    itemKeys.map(key => <Cell key={`cell-${key.key}-${i}`}>
                                        <CardValue>
                                            {editMode ? <Input
                                                border
                                                style={{minWidth: 0}}
                                                value={item[key.key]}
                                                onChange={e => onItemChange(currentInvoiceIndex, i, key.key, e.target.value)}
                                            /> : (key.type === 'amount' ?
                                                formatAmount(item[key.key])
                                                :
                                                item[key.key])}
                                        </CardValue>
                                    </Cell>)
                                }
                            </Row>)
                        }
                    </Table>
                </>)
            } else {
                return null;
            }
        }

        if (section.key === 'other') {
            const others: string[] = [];
            Object.keys(currentInvoice).forEach(key => {
                if (!allKeys[key]) {
                    others.push(key);
                }
            });
            if (others.length > 0) {
                return (<>
                    <Title>{t(section.title)}</Title>
                    {
                        others.map(key => (<CardRow>
                            <CardValue key={`val-${key}`}>
                                {editMode ? <Input
                                    border
                                    value={currentInvoice[key]}
                                    onChange={e => onChange(currentInvoiceIndex, key, e.target.value)}
                                /> : getRow(currentInvoice[key])}
                            </CardValue>
                            <CardKey key={`key-${key}`}>
                                {key}
                            </CardKey>
                        </CardRow>))
                    }
                </>);
            }
            return null;
        }
        const rows: any[] = [];

        section.items?.forEach(item => {
            if (currentInvoice[item.key] !== undefined && currentInvoice[item.key] !== null) {
                rows.push((<CardRow>
                    <CardValue key={`val-${item.key}`}>
                        {editMode ? <Input
                            border
                            value={currentInvoice[item.key]}
                            onChange={e => onChange(currentInvoiceIndex, item.key, e.target.value)}
                        /> : getRow(currentInvoice[item.key], item.type)}
                    </CardValue>
                    <CardKey key={`key-${item.key}`}>
                        {`(${item.key.replaceAll('_', ' ')}) ${t(item.label)}`}
                    </CardKey>
                </CardRow>));
            }
        });

        if (rows.length === 0) return null;

        return <>
            <Title>{t(section.title)}</Title>
            {rows}
        </>
    }

    return (<ValuesList loading={!!loading}>
        {!loading && !data && (!fileLoadError ? <CardList center>
            <PreviewTitle>{fileName}</PreviewTitle>
            <PreviewTitle>{t('PREVIEW')}</PreviewTitle>
            <PreviewSubtitle dir="rtl">{t('PREVIEW_MESSAGE')}</PreviewSubtitle>
            <Button
                narrow
                label={t('UPLOAD')}
                onClick={onStartUpload} />
        </CardList>
            :
        <CardList center>
            <PreviewTitle>{fileName}</PreviewTitle>
            <PreviewTitle>{t('FILE_LOADING_ERROR')}</PreviewTitle>
        </CardList>)
        }
        {!loading && data && <CardList>
            <Row justifyContent="space-between">
                <FormControlLabel
                    id="edit-mode-toggle-control"
                    sx={{ marginLeft: 0, marginRight: 0 }}
                    control={<Switch
                        id="edit-mode-toggle"
                        color="success"
                        checked={editMode}
                        onChange={(e, checked) => toggleEditMode()}
                        inputProps={{'aria-label': 'controlled'}}
                    />}
                    label={t('EDIT_MODE')}
                />
                <Title isMain>{fileName}</Title>
            </Row>
            {entries && <Row justifyContent="space-between" style={{alignItems: 'center'}}>
                <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                    <Button
                        narrow
                        label={t('NEXT')}
                        disabled={currentInvoiceIndex === totalInvoices - 1}
                        onClick={() => setCurrentInvoiceIndex(prev => Math.min(totalInvoices - 1, prev + 1))} />
                    <span style={{fontWeight: 600, minWidth: '40px', textAlign: 'center'}}>
                        {t('DOCUMENT_OF_DOCUMENTS', {index: currentInvoiceIndex + 1, count: totalInvoices})}
                    </span>
                    <Button
                        narrow
                        label={t('PREVIOUS')}
                        disabled={currentInvoiceIndex === 0}
                        onClick={() => setCurrentInvoiceIndex(prev => Math.max(0, prev - 1))} />
                </div>
                <span>{t('PAGES_OF_FILE', {pages: currentEntry?.pages})}</span>
            </Row>}
            {currentEntry?.error && <Toast message={currentEntry.error} title={t('ATTENTION')}/>}
            {entries && currentEntry?.message && <Toast message={currentEntry.message} title={t('DOCUMENT_FAILED')}/>}
            {entries && currentEntry?.priority?.error && <Toast message={currentEntry.priority.error} title={t('ERP_ERROR')}/>}
            <Title>{t('RESULTS')}</Title>
            {
                sections.map(section => getSectionRow(section))
            }
        </CardList>}
        {loading && <>
            <CircularProgress/>
            <LoadingText>{t('LOADING_DATA')}</LoadingText>
        </>}
    </ValuesList>)
}

export default PdfData;
